import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Loading, Avatar } from "@/components/shared/UI";
import { FilterMultiSelect, ClearFiltersButton } from "@/components/form/Fields";
import { usePersistedFilters } from "@/components/hooks/usePersistedFilters";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DeleteButton } from "@/components/shared/DeleteButton";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { useSort, sortRows, SortTh } from "@/components/shared/SortableTable";
import { CampaignModal, CampaignStatusPill } from "@/components/modals/CampaignModal";
import { CAMPAIGN_STATUSES, CAMPAIGN_TYPES } from "@/lib/constants";
import { formatDate, formatMoney } from "@/lib/format";
import { Megaphone, Plus, CalendarDays, List as ListIcon, ChevronLeft, ChevronRight, Trash2, Search, Copy, Send as SendIcon, Repeat } from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const isoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function monthMatrix(monthDate) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const first = new Date(year, month, 1);
  const startDow = (first.getDay() + 6) % 7; // Monday = 0
  const cur = new Date(year, month, 1 - startDow);
  const weeks = [];
  for (let w = 0; w < 6; w++) {
    const days = [];
    for (let d = 0; d < 7; d++) { days.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
    weeks.push(days);
  }
  return weeks;
}

const STATUS_BAR = {
  New: "bg-slate-400", "In Discussions": "bg-amber-500", Scheduled: "bg-sky-500",
  "ON SALE": "bg-emerald-500", Completed: "bg-indigo-500", Extended: "bg-violet-500", Cancelled: "bg-rose-400",
  Preparing: "bg-amber-500", Complete: "bg-indigo-500",
};
const DIRECT_KINDS = ["Property", "Tours/DTR"];
const kindOf = (c) => (c.tours_dtr ? "Tours/DTR" : "Property");
const DIRECT_STATUSES = [...CAMPAIGN_STATUSES, "Preparing", "Complete"];

function CampaignBar({ c, roundL, roundR, onClick }) {
  const useColor = Boolean(c.property_color);
  return (
    <HoverCard openDelay={80} closeDelay={40}>
      <HoverCardTrigger asChild>
        <button
          onClick={() => onClick(c)}
          data-testid={`campaign-bar-${c.id}`}
          style={useColor ? { backgroundColor: c.property_color } : undefined}
          className={`flex h-5 w-full items-center truncate px-1.5 text-left text-[11px] font-medium text-white hover:opacity-90 ${useColor ? "" : (STATUS_BAR[c.effective_status] || "bg-slate-400")} ${roundL ? "rounded-l" : ""} ${roundR ? "rounded-r" : ""}`}
        >
          <span className="truncate">{c.property_name || c.name}</span>
        </button>
      </HoverCardTrigger>
      <CampaignHover c={c} onClick={onClick} />
    </HoverCard>
  );
}

function CampaignHover({ c, onClick }) {
  return (
      <HoverCardContent align="start" className="w-72" data-testid={`campaign-hover-${c.id}`}>
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-2">
            <p className="min-w-0 font-heading text-sm font-semibold text-slate-900">{c.name}</p>
            <CampaignStatusPill status={c.effective_status} />
          </div>
          <div className="space-y-1 text-xs text-slate-600">
            {c.property_name && <p><span className="text-slate-400">Property:</span> {c.property_name}</p>}
            {c.tours_dtr && <p><span className="font-semibold text-violet-600">Tours/DTR</span>{c.group_names?.length ? ` · ${c.group_names.join(", ")}` : ""}</p>}
            {c.tours_dtr && <p><span className="text-slate-400">Sent to:</span> {c.property_names?.join(", ") || "—"}</p>}
            {c.tours_dtr && c.target_audience && <p><span className="text-slate-400">Audience:</span> {c.target_audience}</p>}
            {c.agent_name && <p><span className="text-slate-400">Agent:</span> {c.agent_name}</p>}
            {!c.tours_dtr && <p><span className="text-slate-400">On sale:</span> {formatDate(c.on_sale_start)} → {formatDate(c.on_sale_end)}</p>}
            {c.campaign_type === "Email" && <p><span className="text-slate-400">Send:</span> {formatDate(c.on_sale_start)}{c.send_time ? ` at ${fmtTime(c.send_time)}` : ""}</p>}
            {(c.resend_dates || []).filter(Boolean).sort().map((d, i) => <p key={d}><span className="text-slate-400">Resend {i + 1}{c.resend_labels?.[d] ? ` (${c.resend_labels[d]})` : ""}:</span> {formatDate(d)}{c.send_time ? ` at ${fmtTime(c.send_time)}` : ""}</p>)}
            {(c.markets?.length || c.market_other) && (
              <p><span className="text-slate-400">Market:</span> {[...(c.markets || []).filter((m) => m !== "OTHER"), c.market_other].filter(Boolean).join(", ")}</p>
            )}
            {c.details && <p className="line-clamp-2 text-slate-500">{c.details}</p>}
          </div>
          <Button size="sm" variant="outline" className="w-full" onClick={() => onClick(c)} data-testid={`campaign-hover-view-${c.id}`}>View campaign</Button>
        </div>
      </HoverCardContent>
  );
}

const fadedBg = (c) => (c.property_color && /^#[0-9a-f]{6}$/i.test(c.property_color) ? { backgroundColor: `${c.property_color}33` } : undefined);
const solidBg = (c) => (c.property_color ? { backgroundColor: c.property_color } : undefined);
const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")}${h < 12 ? "am" : "pm"}`;
};
const edmEnd = (c) => [c.on_sale_end, ...(c.resend_dates || [])].filter(Boolean).sort().pop() || c.on_sale_start;

function EdmBar({ c, seg, dayIsos, onClick }) {
  const marks = [{ d: c.on_sale_start, label: "Send" }, ...(c.resend_dates || []).filter(Boolean).sort().map((d, i) => ({ d, label: `Resend ${i + 1}`, text: c.resend_labels?.[d] }))]
    .map((m) => ({ ...m, idx: dayIsos.indexOf(m.d) }))
    .filter((m) => m.idx >= seg.start && m.idx < seg.start + seg.len);
  const statusCls = STATUS_BAR[c.effective_status] || "bg-slate-400";
  const name = c.property_name || c.name;
  const nameAtStart = !marks.some((m) => m.idx === seg.start);
  return (
    <HoverCard openDelay={80} closeDelay={40}>
      <HoverCardTrigger asChild>
        <button onClick={() => onClick(c)} data-testid={`campaign-bar-${c.id}`}
          className={`relative block h-5 w-full overflow-hidden text-left text-[11px] font-medium hover:brightness-95 ${seg.roundL ? "rounded-l" : ""} ${seg.roundR ? "rounded-r" : ""}`}>
          <span className={`absolute inset-0 ${c.property_color ? "" : `${statusCls} opacity-20`}`} style={fadedBg(c)} />
          {nameAtStart && <span className="relative block truncate px-1.5 leading-5 text-slate-600">{name}</span>}
          {marks.map((m) => {
            const isSend = m.label === "Send";
            const pos = { left: `calc(${((m.idx - seg.start) / seg.len) * 100}% + 1px)`, width: `calc(${100 / seg.len}% - 2px)` };
            return isSend ? (
              <span key={m.label} title="Send" data-testid={`campaign-marker-${c.id}-send`}
                className={`absolute inset-y-0 flex items-center gap-1 overflow-hidden rounded px-1.5 text-white shadow-sm ${c.property_color ? "" : statusCls}`}
                style={{ ...solidBg(c), ...pos }}>
                <SendIcon className="h-3 w-3 shrink-0" /><span className="truncate">{name}</span>
              </span>
            ) : (
              <span key={m.label} title={m.text ? `${m.label}: ${m.text}` : m.label} data-testid={`campaign-marker-${c.id}-${m.label.replace(" ", "-").toLowerCase()}`}
                className="absolute inset-y-0 flex min-w-0 items-center gap-1 overflow-hidden px-1.5 text-slate-700" style={pos}>
                <span className={`h-2 w-2 shrink-0 rounded-full ring-2 ring-white ${c.property_color ? "" : statusCls}`} style={solidBg(c)} />
                <Repeat className="h-3 w-3 shrink-0" />
                {!m.text && <span className="text-[10px] font-bold">{m.label.split(" ")[1]}</span>}
                {m.text && <span className="truncate text-[10px] font-medium" data-testid={`campaign-marker-label-${c.id}-${m.label.split(" ")[1]}`}>{m.text}</span>}
              </span>
            );
          })}
        </button>
      </HoverCardTrigger>
      <CampaignHover c={c} onClick={onClick} />
    </HoverCard>
  );
}

export function CalendarView({ campaigns, onOpen, isEDM }) {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const weeks = useMemo(() => monthMatrix(month), [month]);
  const todayIso = isoOf(new Date());

  const layoutWeek = (weekDays) => {
    const dayIsos = weekDays.map(isoOf);
    const ws = dayIsos[0], we = dayIsos[6];
    const segs = [];
    for (const c of campaigns) {
      const s = c.on_sale_start;
      if (!s) continue;
      const e = isEDM ? edmEnd(c) : (c.on_sale_end || c.on_sale_start);
      if (e < ws || s > we) continue;
      let start = dayIsos.findIndex((di) => di >= s);
      if (start < 0) start = 0;
      let end = 6;
      for (let i = 6; i >= 0; i--) { if (dayIsos[i] <= e) { end = i; break; } }
      segs.push({ c, start, len: end - start + 1, roundL: s >= ws, roundR: e <= we });
    }
    segs.sort((a, b) => a.start - b.start || b.len - a.len);
    const lanes = [];
    for (const seg of segs) {
      let lane = lanes.find((l) => l.lastEnd < seg.start);
      if (!lane) { lane = { items: [], lastEnd: -1 }; lanes.push(lane); }
      lane.items.push(seg);
      lane.lastEnd = seg.start + seg.len - 1;
    }
    return lanes;
  };

  return (
    <Card className="overflow-hidden" data-testid="campaign-calendar">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <h3 className="font-heading text-base font-semibold text-slate-900">{MONTHS[month.getMonth()]} {month.getFullYear()}</h3>
        <div className="flex items-center gap-1">
          <button data-testid="cal-prev" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"><ChevronLeft className="h-4 w-4" /></button>
          <button data-testid="cal-today" onClick={() => { const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); }} className="rounded-md px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100">Today</button>
          <button data-testid="cal-next" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50/60 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        {DOW.map((d) => <div key={d} className="py-2">{d}</div>)}
      </div>
      <div>
        {weeks.map((weekDays, wi) => {
          const lanes = layoutWeek(weekDays);
          const shown = lanes;
          const minH = Math.max(96, 30 + shown.length * 22);
          return (
            <div key={wi} className="relative">
              <div className="grid grid-cols-7">
                {weekDays.map((day, di) => {
                  const dayIso = isoOf(day);
                  const inMonth = day.getMonth() === month.getMonth();
                  return (
                    <div key={di} style={{ minHeight: minH }} className={`border-b border-r border-slate-100 p-1 ${inMonth ? "bg-white" : "bg-slate-50/40"}`}>
                      <div className={`text-right text-[11px] font-semibold ${dayIso === todayIso ? "text-sky-600" : inMonth ? "text-slate-500" : "text-slate-300"}`}>
                        {dayIso === todayIso ? <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-sky-600 text-white">{day.getDate()}</span> : day.getDate()}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="pointer-events-none absolute inset-x-0 top-7 space-y-0.5 px-0.5">
                {shown.map((lane, li) => (
                  <div key={li} className="grid grid-cols-7 gap-0.5">
                    {lane.items.map((seg) => (
                      <div key={seg.c.id} className="pointer-events-auto min-w-0" style={{ gridColumn: `${seg.start + 1} / span ${seg.len}` }}>
                        {isEDM ? <EdmBar c={seg.c} seg={seg} dayIsos={weekDays.map(isoOf)} onClick={onOpen} />
                          : <CampaignBar c={seg.c} roundL={seg.roundL} roundR={seg.roundR} onClick={onOpen} />}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

const ACCESSORS = {
  name: (c) => c.name,
  results_bookings: (c) => c.results_bookings,
  results_room_nights: (c) => c.results_room_nights,
  agent_name: (c) => c.agent_name,
  property_name: (c) => c.property_name,
  on_sale_start: (c) => c.on_sale_start,
  effective_status: (c) => c.effective_status,
  results_revenue: (c) => (c.results_revenue != null ? Number(c.results_revenue) : null),
};

function AssignedAvatars({ userIds, usersById }) {
  const list = (userIds || []).map((id) => usersById[id]).filter(Boolean);
  if (list.length === 0) return <span className="text-slate-300">—</span>;
  return (
    <div className="flex -space-x-1.5" data-testid="campaign-assigned-users">
      {list.slice(0, 4).map((u) => (
        <Avatar key={u.id} name={u.name} color={u.color} user className="h-7 w-7 text-[10px] ring-2 ring-white" />
      ))}
      {list.length > 4 && (
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-[10px] font-bold text-slate-500 ring-2 ring-white">+{list.length - 4}</span>
      )}
    </div>
  );
}

const PAGE = 50;
function ListView({ campaigns, onOpen, onRefresh, onDuplicate, sort, onSort, isEDM, usersById }) {
  const [limit, setLimit] = useState(PAGE);
  if (campaigns.length === 0) return <Card className="p-10 text-center text-sm text-slate-400">No campaigns match.</Card>;
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="campaigns-table">
          <thead>
            <tr className="border-b border-slate-100 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
              <SortTh label="Campaign" sortKey="name" sort={sort} onSort={onSort} />
              {isEDM ? (
                <th className="px-5 py-3">Assigned User</th>
              ) : (
                <SortTh label="Agent" sortKey="agent_name" sort={sort} onSort={onSort} />
              )}
              <SortTh label="Property" sortKey="property_name" sort={sort} onSort={onSort} />
              {!isEDM && <th className="px-5 py-3">Market</th>}
              <SortTh label="On Sale" sortKey="on_sale_start" sort={sort} onSort={onSort} />
              <SortTh label="Status" sortKey="effective_status" sort={sort} onSort={onSort} />
              <SortTh label="Bookings" sortKey="results_bookings" sort={sort} onSort={onSort} />
              <SortTh label="Room Nights" sortKey="results_room_nights" sort={sort} onSort={onSort} />
              <SortTh label="Revenue" sortKey="results_revenue" sort={sort} onSort={onSort} />
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {sortRows(campaigns, sort, ACCESSORS).slice(0, limit).map((c) => (
              <tr key={c.id} onClick={() => onOpen(c)} data-testid={`campaign-row-${c.id}`} className="group cursor-pointer border-b border-slate-50 hover:bg-slate-50/70">
                <td className="px-5 py-3 font-medium text-slate-800">{c.name}</td>
                {isEDM ? (
                  <td className="px-5 py-3"><AssignedAvatars userIds={c.user_ids} usersById={usersById} /></td>
                ) : (
                  <td className="px-5 py-3 text-slate-600">{c.agent_name || "—"}</td>
                )}
                <td className="px-5 py-3 text-slate-600">{c.tours_dtr ? (
                  <span title={c.property_names?.length ? `Sent to: ${c.property_names.join(", ")}` : undefined} data-testid={`campaign-tours-${c.id}`}>
                    <span className="mr-1.5 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700">Tours/DTR</span>{c.group_names?.join(", ") || "—"}
                  </span>) : (c.property_name || "—")}</td>
                {!isEDM && <td className="px-5 py-3 text-slate-600">{[...(c.markets || []).filter((m) => m !== "OTHER"), c.market_other].filter(Boolean).join(", ") || "—"}</td>}
                <td className="whitespace-nowrap px-5 py-3 text-slate-600">{!c.on_sale_start ? "—" : c.tours_dtr ? `Send ${formatDate(c.on_sale_start)}${c.send_time ? ` at ${fmtTime(c.send_time)}` : ""}` : `${formatDate(c.on_sale_start)} → ${formatDate(c.on_sale_end)}`}</td>
                <td className="px-5 py-3"><CampaignStatusPill status={c.effective_status} /></td>
                <td className="px-5 py-3 tabular-nums text-slate-600" data-testid={`campaign-bookings-${c.id}`}>{c.results_bookings != null ? Number(c.results_bookings).toLocaleString("en-AU") : "—"}</td>
                <td className="px-5 py-3 tabular-nums text-slate-600" data-testid={`campaign-nights-${c.id}`}>{c.results_room_nights != null ? Number(c.results_room_nights).toLocaleString("en-AU") : "—"}</td>
                <td className="px-5 py-3 tabular-nums text-slate-600">{c.results_revenue != null ? formatMoney(c.results_revenue) : "—"}</td>
                <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => onDuplicate(c)} title="Duplicate campaign" className="rounded-md p-1.5 text-slate-400 opacity-0 transition-opacity hover:bg-sky-50 hover:text-sky-600 group-hover:opacity-100" data-testid={`campaign-duplicate-${c.id}`}><Copy className="h-4 w-4" /></button>
                    <DeleteButton
                      endpoint="/campaigns" id={c.id} label="campaign" onDeleted={onRefresh}
                      recurringSeries={!!c.series_id} scopeOptions={[["this", "Just this campaign"], ["future", "This and all future campaigns in the series"]]}
                      trigger={<button className="rounded-md p-1.5 text-slate-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100" data-testid={`campaign-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {campaigns.length > limit && (
        <div className="flex items-center justify-center gap-3 border-t border-slate-100 py-3 text-xs text-slate-500">
          Showing {limit} of {campaigns.length}
          <button onClick={() => setLimit((l) => l + PAGE)} className="font-semibold text-sky-600 hover:underline" data-testid="campaigns-show-more">Show more</button>
          <button onClick={() => setLimit(campaigns.length)} className="font-semibold text-sky-600 hover:underline" data-testid="campaigns-show-all">Show all</button>
        </div>
      )}
    </Card>
  );
}

export default function Campaigns({ mode = "agent" }) {
  const isEDM = mode === "edm";
  const { users } = useAuth();
  const [rows, setRows] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [view, setView] = useState("list");
  const [filters, setFilters] = usePersistedFilters(isEDM ? "edm_campaign_filters" : "agent_campaign_filters", { status: [], property_id: [], campaign_type: [], user_ids: [] });
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const { sort, onSort } = useSort("on_sale_start", "desc");
  const [searchParams, setSearchParams] = useSearchParams();

  const usersById = useMemo(() => Object.fromEntries((users || []).map((u) => [u.id, u])), [users]);

  const load = useCallback(() => {
    setRows(null);
    client.get("/campaigns", { params: { kind: isEDM ? "edm" : "agent" } }).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [isEDM]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { client.get("/organisations", { params: { lite: true } }).then(({ data }) => setOrgs(data)).catch(() => {}); }, []);

  const openId = searchParams.get("open");
  useEffect(() => {
    if (!openId || !rows) return;
    const c = rows.find((r) => r.id === openId);
    if (c) { setView("list"); setModal({ record: c }); }
    searchParams.delete("open");
    setSearchParams(searchParams, { replace: true });
  }, [openId, rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const setF = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  const propertyOpts = orgs.filter((o) => o.org_type === "DTR Property" || o.name === "Dreamtime Resorts").map((o) => ({ value: o.id, label: o.name }));
  const agentTypeOpts = CAMPAIGN_TYPES.filter((t) => t !== "Email");

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = search.trim().toLowerCase();
    return rows.filter((c) => {
      const type = c.campaign_type || "Agent";
      if (isEDM ? type !== "Email" : type === "Email") return false;
      return (
        (!filters.status.length || filters.status.includes(c.effective_status)) &&
        (!filters.property_id.length || filters.property_id.includes(c.property_id)) &&
        (!filters.campaign_type?.length || filters.campaign_type.includes(isEDM ? kindOf(c) : type)) &&
        (!isEDM || !filters.user_ids?.length || (c.user_ids || []).some((u) => filters.user_ids.includes(u))) &&
        (!q || [
          c.name, c.agent_name, c.property_name, c.subject, c.details, c.notes,
          c.market_other, ...(c.markets || []), ...(c.group_names || []), ...(c.property_names || []), c.target_audience,
        ].filter(Boolean).join(" ").toLowerCase().includes(q))
      );
    });
  }, [rows, filters, search, isEDM]);

  const openCampaign = (c) => setModal({ record: c });

  const duplicateCampaign = (c) => {
    const skip = new Set(["id", "created_at", "updated_at", "created_by_id", "updated_by_id", "agent_name", "property_name", "effective_status", "property_color"]);
    const prefill = {};
    Object.entries(c).forEach(([k, v]) => {
      if (skip.has(k) || k.startsWith("results_")) return;
      prefill[k] = v;
    });
    prefill.name = `${c.name || "Campaign"} (Copy)`;
    setModal({ prefill });
  };

  const title = isEDM ? "Direct Campaigns" : "Agent Campaigns";
  const subtitle = isEDM ? "Plan and track direct campaigns across your properties" : "Plan and track agent campaigns across your properties";

  return (
    <div className="space-y-6">
      <PageHeader title={title} subtitle={subtitle} actions={
        <Button className="bg-sky-600 hover:bg-sky-700" onClick={() => setModal({ prefill: { campaign_type: isEDM ? "Email" : "Agent" } })} data-testid="campaigns-add">
          <Plus className="mr-1.5 h-4 w-4" /> New Campaign
        </Button>
      } />

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
          <button data-testid="campaigns-view-calendar" onClick={() => setView("calendar")} className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${view === "calendar" ? "bg-sky-50 text-sky-700" : "text-slate-500 hover:text-slate-800"}`}>
            <CalendarDays className="h-4 w-4" /> Calendar
          </button>
          <button data-testid="campaigns-view-list" onClick={() => setView("list")} className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${view === "list" ? "bg-sky-50 text-sky-700" : "text-slate-500 hover:text-slate-800"}`}>
            <ListIcon className="h-4 w-4" /> List
          </button>
        </div>
        <FilterMultiSelect className="w-52" placeholder="All Properties" value={filters.property_id} onChange={setF("property_id")} options={propertyOpts} testid="campaigns-filter-property" selectAll />
        {!isEDM && <FilterMultiSelect className="w-40" placeholder="All Types" value={filters.campaign_type} onChange={setF("campaign_type")} options={agentTypeOpts} testid="campaigns-filter-type" />}
        {isEDM && <FilterMultiSelect className="w-44" placeholder="All Campaign Types" value={(filters.campaign_type || []).filter((v) => DIRECT_KINDS.includes(v))} onChange={setF("campaign_type")} options={DIRECT_KINDS} testid="campaigns-filter-kind" />}
        {isEDM && <FilterMultiSelect className="w-44" placeholder="All Users" value={filters.user_ids || []} onChange={setF("user_ids")} options={(users || []).map((u) => ({ value: u.id, label: u.name }))} testid="campaigns-filter-users" />}
        <FilterMultiSelect className="w-44" placeholder="All Statuses" value={filters.status} onChange={setF("status")} options={isEDM ? DIRECT_STATUSES : CAMPAIGN_STATUSES} testid="campaigns-filter-status" />
        {view === "list" && (
          <div className="relative w-64">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search campaigns…"
              className="pl-9"
              data-testid="campaigns-search"
            />
          </div>
        )}
        <ClearFiltersButton active={Boolean(search) || filters.status.length > 0 || filters.property_id.length > 0 || (filters.campaign_type?.length || 0) > 0 || (filters.user_ids?.length || 0) > 0} onClick={() => { setSearch(""); setFilters({ status: [], property_id: [], campaign_type: [], user_ids: [] }); }} testid="campaigns-clear" />
      </div>

      {!rows ? <Loading /> : view === "calendar" ? (
        <CalendarView campaigns={filtered} onOpen={openCampaign} isEDM={isEDM} />
      ) : (
        <ListView campaigns={filtered} onOpen={openCampaign} onRefresh={load} onDuplicate={duplicateCampaign} sort={sort} onSort={onSort} isEDM={isEDM} usersById={usersById} />
      )}

      {modal && (
        <CampaignModal open record={modal.record} prefill={modal.prefill} onClose={() => setModal(null)} onSuccess={load} />
      )}
    </div>
  );
}
